import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, Clock, AlertCircle, MapPin, ArrowRight,
  ClipboardList, Activity, RefreshCw, User, Calendar,
  ChevronRight
} from 'lucide-react';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import { farmTasksAPI } from '../../services/portalAPI';
import farmMapAPI, { GrazingSession } from '../../services/farmMapAPI';
import './FarmCrewDashboard.css';

interface FarmTask {
  id: number;
  title: string;
  description: string;
  due_date: string | null;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  zone_name?: string;
  assigned_to_name?: string;
  created_by_name?: string;
}

const PRIORITY_COLORS: Record<string, string> = {
  low: '#10B981',
  medium: '#3B82F6',
  high: '#F59E0B',
  urgent: '#EF4444',
};

const PRIORITY_LABELS: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

const FarmCrewDashboard: React.FC = () => {
  const { user } = usePortalAuth();
  const [myTasks, setMyTasks] = useState<FarmTask[]>([]);
  const [allPendingTasks, setAllPendingTasks] = useState<FarmTask[]>([]);
  const [activeSessions, setActiveSessions] = useState<GrazingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tasksRes, sessionsData] = await Promise.all([
        farmTasksAPI.getAll({ status: 'pending' }),
        farmMapAPI.getSessions({ active: true }),
      ]);

      const allTasks = tasksRes.data || [];

      // Filter my tasks (assigned to me) vs all pending tasks
      const myAssigned = allTasks.filter((t: FarmTask) => t.assigned_to_name === user?.name);
      const otherPending = allTasks.filter((t: FarmTask) => t.assigned_to_name !== user?.name);

      setMyTasks(myAssigned);
      setAllPendingTasks(otherPending);
      setActiveSessions(sessionsData);
    } catch (error) {
      console.error('Error loading crew dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.name]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCompleteTask = async (taskId: number) => {
    setCompleting(taskId);
    try {
      await farmTasksAPI.complete(taskId);
      await loadData();
    } catch (error) {
      console.error('Error completing task:', error);
      alert('Failed to complete task. Please try again.');
    } finally {
      setCompleting(null);
    }
  };

  const handleStartTask = async (taskId: number) => {
    try {
      await farmTasksAPI.update(taskId, { status: 'in_progress' });
      await loadData();
    } catch (error) {
      console.error('Error starting task:', error);
    }
  };

  const formatDueDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date < today) return 'Overdue';
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const isOverdue = (dateStr: string | null): boolean => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  if (loading) {
    return (
      <div className="crew-dashboard">
        <div className="crew-dashboard-loading">
          <RefreshCw className="spin" size={24} />
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const totalPendingTasks = myTasks.length + allPendingTasks.length;
  const urgentTasks = [...myTasks, ...allPendingTasks].filter(t => t.priority === 'urgent' || isOverdue(t.due_date));

  return (
    <div className="crew-dashboard">
      {/* Welcome Header */}
      <div className="crew-header">
        <div className="crew-header-content">
          <h1>Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 18 ? 'Afternoon' : 'Evening'}, {user?.name?.split(' ')[0] || 'Team Member'}</h1>
          <p className="crew-header-date">
            <Calendar size={16} />
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <button onClick={loadData} className="crew-refresh-btn" title="Refresh">
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Quick Stats */}
      <div className="crew-stats">
        <div className="crew-stat">
          <ClipboardList size={20} />
          <div className="crew-stat-content">
            <span className="crew-stat-value">{myTasks.length}</span>
            <span className="crew-stat-label">My Tasks</span>
          </div>
        </div>
        <div className="crew-stat">
          <Activity size={20} />
          <div className="crew-stat-content">
            <span className="crew-stat-value">{activeSessions.length}</span>
            <span className="crew-stat-label">Active Batches</span>
          </div>
        </div>
        {urgentTasks.length > 0 && (
          <div className="crew-stat crew-stat-urgent">
            <AlertCircle size={20} />
            <div className="crew-stat-content">
              <span className="crew-stat-value">{urgentTasks.length}</span>
              <span className="crew-stat-label">Urgent</span>
            </div>
          </div>
        )}
      </div>

      {/* My Tasks Section */}
      <section className="crew-section">
        <div className="crew-section-header">
          <h2>
            <User size={18} />
            My Tasks
          </h2>
          {/* "View All" link to /portal/farm-tasks hidden while Farmhand is
              paused for the Farmer's Partner rebuild (Feb 2027). */}
        </div>

        {myTasks.length === 0 ? (
          <div className="crew-empty">
            <CheckCircle2 size={32} />
            <p>No tasks assigned to you right now</p>
          </div>
        ) : (
          <div className="crew-task-list">
            {myTasks.slice(0, 5).map((task) => (
              <div
                key={task.id}
                className={`crew-task-card ${task.status === 'in_progress' ? 'in-progress' : ''}`}
              >
                <div className="crew-task-priority" style={{ backgroundColor: PRIORITY_COLORS[task.priority] }} />
                <div className="crew-task-content">
                  <div className="crew-task-title">{task.title}</div>
                  {task.description && (
                    <div className="crew-task-desc">{task.description}</div>
                  )}
                  <div className="crew-task-meta">
                    {task.zone_name && (
                      <span className="crew-task-zone">
                        <MapPin size={12} />
                        {task.zone_name}
                      </span>
                    )}
                    {task.due_date && (
                      <span className={`crew-task-due ${isOverdue(task.due_date) ? 'overdue' : ''}`}>
                        <Clock size={12} />
                        {formatDueDate(task.due_date)}
                      </span>
                    )}
                    <span className="crew-task-badge" style={{ backgroundColor: PRIORITY_COLORS[task.priority] }}>
                      {PRIORITY_LABELS[task.priority]}
                    </span>
                  </div>
                </div>
                <div className="crew-task-actions">
                  {task.status === 'pending' && (
                    <button
                      onClick={() => handleStartTask(task.id)}
                      className="crew-btn crew-btn-start"
                      title="Start working"
                    >
                      <ArrowRight size={16} />
                      Start
                    </button>
                  )}
                  <button
                    onClick={() => handleCompleteTask(task.id)}
                    className="crew-btn crew-btn-complete"
                    disabled={completing === task.id}
                    title="Mark complete"
                  >
                    <CheckCircle2 size={16} />
                    {completing === task.id ? '...' : 'Done'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Active Grazing Sessions */}
      <section className="crew-section">
        <div className="crew-section-header">
          <h2>
            <Activity size={18} />
            Active Batches
          </h2>
          <Link to="/portal/farmmap" className="crew-section-link">
            Farm Map
            <ChevronRight size={16} />
          </Link>
        </div>

        {activeSessions.length === 0 ? (
          <div className="crew-empty">
            <MapPin size={32} />
            <p>No active grazing sessions</p>
          </div>
        ) : (
          <div className="crew-session-list">
            {activeSessions.map((session) => (
              <Link
                key={session.id}
                to="/portal/farmmap"
                className="crew-session-card"
              >
                <div className="crew-session-content">
                  <div className="crew-session-name">
                    {session.batch_name || `Batch ${session.id}`}
                  </div>
                  <div className="crew-session-info">
                    <span>{session.animal_count} {session.animal_type}</span>
                    <span className="crew-session-dot">•</span>
                    <span>{session.zone_name}</span>
                  </div>
                  <div className="crew-session-meta">
                    <span>Day {session.days_grazed || 1}</span>
                    {session.shelter_name && (
                      <>
                        <span className="crew-session-dot">•</span>
                        <span>{session.shelter_count > 1 ? `${session.shelter_count}× ` : ''}{session.shelter_name}</span>
                      </>
                    )}
                  </div>
                </div>
                <ChevronRight size={20} className="crew-session-arrow" />
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Other Pending Tasks (unassigned or assigned to others) */}
      {allPendingTasks.length > 0 && (
        <section className="crew-section">
          <div className="crew-section-header">
            <h2>
              <ClipboardList size={18} />
              Other Open Tasks
            </h2>
            <span className="crew-section-count">{allPendingTasks.length}</span>
          </div>

          <div className="crew-task-list crew-task-list-compact">
            {allPendingTasks.slice(0, 5).map((task) => (
              <div key={task.id} className="crew-task-card crew-task-card-compact">
                <div className="crew-task-priority" style={{ backgroundColor: PRIORITY_COLORS[task.priority] }} />
                <div className="crew-task-content">
                  <div className="crew-task-title">{task.title}</div>
                  <div className="crew-task-meta">
                    {task.assigned_to_name && (
                      <span className="crew-task-assignee">
                        <User size={12} />
                        {task.assigned_to_name}
                      </span>
                    )}
                    {task.due_date && (
                      <span className={`crew-task-due ${isOverdue(task.due_date) ? 'overdue' : ''}`}>
                        <Clock size={12} />
                        {formatDueDate(task.due_date)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {/* "View N more tasks" link to /portal/farm-tasks hidden while
                Farmhand is paused for the Farmer's Partner rebuild (Feb 2027). */}
          </div>
        </section>
      )}

      {/* Quick Actions */}
      <section className="crew-quick-actions">
        <Link to="/portal/farmmap" className="crew-action-card">
          <MapPin size={24} />
          <span>Farm Map</span>
        </Link>
        {/* "All Tasks" link to /portal/farm-tasks hidden while Farmhand is
            paused for the Farmer's Partner rebuild (Feb 2027). */}
      </section>
    </div>
  );
};

export default FarmCrewDashboard;
